import type { ChatMessage } from "../types/redux";
import {
  attachIdentityToActivity,
  getConversationMetadata,
  getDirectLine,
} from "../directLlineClient";
import { acquireDelegatedGraphToken, getCurrentMsalAccount } from "../utils/msalTokenService";
import { acquireProjectSwitchLock } from "../projectSwitchLock";
import { clearResults } from "../ResultsSlice_Store";
import {
  markInitialized,
  selectCurrentProjectType,
  setCurrentProject,
  upsertUserProject,
} from "../ProjectSlice_Store";
import { setLayoutMode } from "../LayoutSlice_Store";
import { closeAllDocumentPreviews } from "../DocumentPreviewSlice_Store";
import {
  resetChatForNewProject,
  setBootstrapError,
  setBootstrapInitialized,
  setChatHistoryError,
  setChatHistoryLoading,
} from "./chatSlice";
import { cancelOngoingConversationLoad, resetChatModuleState } from "./chatModuleState";
import { initializeChatListener } from "./chatListener";
import type { ChatDispatch } from "./chatSliceTypes";

const TEMP_PROJECT_PREFIX = "temp-";
const RANDOM_SUFFIX_LENGTH = -4;

// A type alias, not an interface: only aliases get the implicit index signature
// that `Project` (a Record-like type) requires of its arguments.
type PlaceholderProject = {
  id: string;
  name: string;
  summary: string;
  isTemporary: boolean;
};

/** A date-based placeholder the backend replaces with a real project on bootstrap. */
function createPlaceholderProject(): PlaceholderProject {
  const now = new Date();
  const day = now.getDate();
  const month = now.getMonth() + 1; // JS months are 0-indexed
  const year = String(now.getFullYear()).slice(-2); // Last 2 digits

  return {
    id: `${TEMP_PROJECT_PREFIX}${now.getTime()}-${Math.random().toString(36).slice(RANDOM_SUFFIX_LENGTH)}`,
    name: `New ${day}/${month}/${year}`,
    summary: "",
    isTemporary: true,
  };
}

/**
 * Steps 0 and 1: cancel any in-flight load and clear every slice the old
 * project touched. All of it is synchronous by design — see startNewProject.
 */
function clearWorkspaceForNewProject(dispatch: ChatDispatch): void {
  console.log("🔄 [NEW CHAT] Step 0: Cancelling any ongoing conversation load");
  cancelOngoingConversationLoad();
  dispatch(setChatHistoryLoading(false));
  dispatch(setChatHistoryError(null));

  console.log("🔄 [NEW CHAT] Step 1: Resetting all Redux state synchronously");
  dispatch(resetChatForNewProject());
  dispatch(clearResults());
  dispatch(closeAllDocumentPreviews());
  dispatch(setLayoutMode("chat"));
}

/** Step 3: park the UI on a placeholder until bootstrap returns the real project. */
function installPlaceholderProject(dispatch: ChatDispatch): void {
  const placeholderProject = createPlaceholderProject();
  console.log(
    "🔄 [NEW CHAT] Step 3: Setting temporary project state:",
    placeholderProject.name,
    placeholderProject.id
  );

  dispatch(setCurrentProject(placeholderProject));
  dispatch(upsertUserProject({ project: { ...placeholderProject }, prepend: true }));
  dispatch(markInitialized(false));
}

/** An untouched new chat is already the destination — nothing to reset. */
function isEmptyNewChat(state: any): boolean {
  const messages: ChatMessage[] = state.chatSlice?.messages || [];
  const hasUserMessage = messages.some((msg: ChatMessage) => msg.sender === "user");
  return !hasUserMessage && !state.chatSlice?.chatHistoryLoading;
}

/**
 * Start a new project.
 *
 * IMPORTANT: all synchronous cleanup runs BEFORE any async operation, so the
 * workspace is always cleared even if a later step fails (e.g. after an auth
 * timeout or a DirectLine connection problem).
 */
export const startNewProject =
  () =>
  async (dispatch: any, getState: any): Promise<void> => {
    console.log("🔄 [NEW CHAT] startNewProject thunk called");

    if (isEmptyNewChat(getState())) {
      console.log("⏭️ [NEW CHAT] Already on an empty new chat — skipping");
      return;
    }

    // Acquire project switch lock — aborts any in-flight project switch
    const signal = acquireProjectSwitchLock();
    clearWorkspaceForNewProject(dispatch);

    try {
      // Step 2: async, and can fail — but the workspace is already clean
      console.log("🔄 [NEW CHAT] Step 2: Resetting module-level state");
      await resetChatModuleState();
      if (signal.aborted) {
        console.log("🔒 [NEW CHAT] Superseded after resetChatModuleState — aborting");
        return;
      }

      installPlaceholderProject(dispatch);

      // Step 4: reinitialize the chat listener, which triggers bootstrap
      console.log("🔄 [NEW CHAT] Step 4: Reinitializing chat listener");
      await dispatch(initializeChatListener(signal));
      if (signal.aborted) {
        console.log("🔒 [NEW CHAT] Superseded after initializeChatListener — aborting");
        return;
      }
      console.log("✅ [NEW CHAT] startNewProject completed successfully");
    } catch (error) {
      if (signal.aborted) return;
      console.error("❌ [NEW CHAT] startNewProject failed:", error);
      dispatch(
        setBootstrapError(
          `Failed to start new project: ${(error as Error).message || "Unknown error"}`
        )
      );
    }
  };

/**
 * A permanent project ID lets the backend rehydrate the conversation. A
 * temporary one must NOT be sent — the backend mints the real UUID — but its
 * name is passed along so the new project keeps the label the user sees.
 */
function attachProjectIdentity(channelData: Record<string, unknown>, state: any): void {
  const currentProjectId = state.projectSlice?.currentProjectId;
  const currentProjectName = state.projectSlice?.currentProject?.name;
  if (!currentProjectId) return;

  if (!currentProjectId.startsWith(TEMP_PROJECT_PREFIX)) {
    console.log(
      "🚀 [BOOTSTRAP] Including project_id in bootstrap (permanent project):",
      currentProjectId
    );
    channelData.project_id = currentProjectId;
    return;
  }

  console.log(
    "🚀 [BOOTSTRAP] Skipping project_id in bootstrap (temporary project - backend will generate UUID):",
    currentProjectId
  );
  if (currentProjectName) {
    console.log("🚀 [BOOTSTRAP] Including project_label for new project:", currentProjectName);
    channelData.project_label = currentProjectName;
  }
}

/** The control payload that tells the core agent who and what this session is. */
function buildBootstrapChannelData(state: any): Record<string, unknown> {
  const { userName, userEmail } = state.userAuthAndAdminSlice;
  const { mcpEnvironment, dataVersion, selectedUseCase } = state.chatSlice;

  const channelData: Record<string, unknown> = {
    message_type: "control",
    control_event: "bootstrap",
    username: userName || userEmail,
    user_email: userEmail,
    mcp_environment: mcpEnvironment,
    data_version: dataVersion,
    // selectedUseCase for a new project, project type for a reloaded one
    use_case: selectedUseCase || selectCurrentProjectType(state),
  };

  console.log("🚀 [BOOTSTRAP] User info:", {
    userName,
    userEmail,
    currentProjectId: state.projectSlice?.currentProjectId,
    currentProjectName: state.projectSlice?.currentProject?.name,
    effectiveUseCase: channelData.use_case,
  });

  attachProjectIdentity(channelData, state);
  return channelData;
}

/**
 * Phase 1: stamp the bootstrap with a delegated Graph token. Unlike a user
 * message this is best-effort — an unidentified bootstrap still beats none.
 */
async function withBootstrapIdentity(activity: any): Promise<any> {
  try {
    console.log("🚀 [BOOTSTRAP] Acquiring delegated Graph token");
    const delegatedToken = await acquireDelegatedGraphToken();
    const identified = attachIdentityToActivity(activity, delegatedToken);
    console.log("✅ [BOOTSTRAP] Attached user identity to bootstrap message");
    return identified;
  } catch (tokenError) {
    console.warn(
      "⚠️ [BOOTSTRAP] Failed to acquire delegated token for bootstrap - sending without identity:",
      tokenError
    );
    return activity;
  }
}

/** Announce the session to the core agent, once per session. */
export const initializeCoreAgentBootstrap =
  () =>
  async (dispatch: ChatDispatch, getState: any): Promise<void> => {
    console.log("🚀 [BOOTSTRAP] initializeCoreAgentBootstrap called");
    const state = getState();

    // Only run once per session
    if (state.chatSlice.bootstrapInitialized) {
      console.log("⚠️ [BOOTSTRAP] Bootstrap already initialized - returning early");
      return;
    }

    try {
      console.log("🚀 [BOOTSTRAP] Setting bootstrapInitialized flag to true");
      dispatch(setBootstrapInitialized(true));

      // Phase 2: MSAL account FIRST — needed for APIM authentication
      console.log("🚀 [BOOTSTRAP] Getting MSAL account");
      const msalAccount = getCurrentMsalAccount();

      console.log("🚀 [BOOTSTRAP] Getting conversation metadata");
      const conversationData = await getConversationMetadata(msalAccount);
      console.log("🚀 [BOOTSTRAP] Conversation metadata received:", conversationData);

      // Should already be initialized by getConversationMetadata
      console.log("🚀 [BOOTSTRAP] Getting DirectLine connection");
      const directLine = await getDirectLine(null, msalAccount);

      const activity = await withBootstrapIdentity({
        from: { role: "user" },
        type: "message",
        text: "CONTROL_CORE_AGENT_BOOTSTRAP",
        channelData: buildBootstrapChannelData(state),
      });

      console.log("🚀 [BOOTSTRAP] Posting bootstrap activity to DirectLine");
      directLine.postActivity(activity).subscribe({
        next: (id: string) =>
          console.log("✅ [BOOTSTRAP] Bootstrap activity posted successfully, ID:", id),
        error: (err: any) => {
          console.error("❌ [BOOTSTRAP] Failed to post bootstrap activity:", err);
          dispatch(setBootstrapError("Failed to send bootstrap message"));
        },
      });
    } catch (err) {
      console.error("❌ [BOOTSTRAP] Bootstrap initialization failed:", err);
      dispatch(setBootstrapError((err as Error).message || "Unknown error"));
    }
  };
